/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description CountryUserRoles Creation View Page.
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Input, Select, Form } from 'antd';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import FormGenerator from '@nephro/components/FormGenerator';

// Helpers
import {
  bottomOptionsCreateEdit,
  onGetItemById
} from '@nephro/commons/helpers/utils';

// Styles
import styles from './styles.module.css';

const { Option } = Select;

/**
 * CountryUserRoles Creation View
 * @param { Object } countryUserRoleActions - CountryUserRole Actions.
 * @param { Object } actions - App Actions.
 * @return { Node } React node.
 */
const CountryUserRolesCreationView = ({
  countryUserRoleActions,
  roleActions,
  authActions,
  profileActions,
  dashboardStaffActions
}) => {
  const { getAdmonCodesByEntity } = dashboardStaffActions;
  const [rolesList, setRolesList] = React.useState([]);
  const [usersList, setUsersList] = React.useState(null);
  const [profilesList, setProfilesList] = React.useState(null);
  const [form] = Form.useForm();
  const history = useHistory();
  const { createCountryUserRole } = countryUserRoleActions;
  const { getRolesArray } = roleActions;
  const { getUserList } = authActions;
  const [clinicsList, setClinicsList] = React.useState([]);
  const { getProfilesArray } = profileActions;
  const { t: translate } = useTranslation();

  const onGoBack = () => {
    history.goBack();
  };

  // Function to get and set users list
  const onGetUsersList = (_, response) => {
    if (response?.data) {
      setUsersList(response?.data);
    }
  };

  const onSearch = (val) => {
    if (val.length >= 3) {
      const isLocal = window.location.hostname === 'localhost';
      const identityProvider = isLocal ? 'OKTA_PROVIDER' : 'LDAP_PROVIDER';
      getUserList({ identityProvider, filter: val }, onGetUsersList);
    } else {
      setUsersList(null);
    }
  };

  // Function to get and set roles list
  const onGetRolesList = (_, response) => {
    if (response?.data?.content) {
      setRolesList(response.data);
    } else {
      onGoBack();
    }
  };

  // Function to get and set profiles list
  const onGetProfilesList = (_, response) => {
    if (response?.data?.content) {
      setProfilesList(response.data);
    } else {
      onGoBack();
    }
  };

  // Function to get and set clinic list
  const onGetClinicsList = (_, response) => {
    if (response?.data) {
      setClinicsList(response.data);
    }
  };

  React.useEffect(() => {
    const entityId = 2;
    getRolesArray({ paged: false }, onGetRolesList);
    getProfilesArray({ paged: false }, onGetProfilesList);
    getAdmonCodesByEntity({ entityId }, onGetClinicsList);
  }, []);

  // Function to get on submit form values
  const onFinish = (values) => {
    const {
      roleName,
      userName,
      profile: profileId,
      name,
      lastName,
      clinicId,
      email
    } = values;

    const profile = onGetItemById(
      profilesList?.content,
      'id_profile',
      profileId
    );
    const role = onGetItemById(rolesList?.content, 'role_id', roleName);
    const userFound = usersList.find(({ username }) => username === userName);
    const user = {
      active: true,
      first_name: name,
      last_name: lastName,
      user_data_id: null,
      username: userFound.username,
      email
    };

    const data = {
      country: {
        country_id: 1,
        name: 'colombia'
      },
      id_clinic: clinicId,
      profile,
      role,
      user
    };
    createCountryUserRole(data, onGoBack);
  };

  // Items to list on form
  const formItems = [
    {
      item: (
        <Select
          showSearch
          placeholder={translate('curMg.roleName')}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {rolesList?.content?.map((item, index) => (
            <Option key={index} value={item.role_id}>
              {item.name}
            </Option>
          ))}
        </Select>
      ),
      name: ['roleName'],
      label: translate('curMg.roleName'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: (
        <Select
          showSearch
          onSearch={onSearch}
          placeholder={translate('curMg.userName')}
          optionFilterProp="children"
          onChange={(userName) => {
            const { firstName, lastName, email } = usersList.find(
              ({ username }) => username === userName
            );
            form.setFieldsValue({
              ...form,
              name: firstName,
              lastName,
              email
            });
          }}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {usersList?.map((item, index) => (
            <Option key={index} value={item.username}>
              {item.username}
            </Option>
          ))}
        </Select>
      ),
      name: ['userName'],
      label: translate('curMg.userName'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: (
        <Select
          showSearch
          placeholder={translate('curMg.profile')}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {profilesList?.content?.map((item, index) => (
            <Option key={index} value={item.id_profile}>
              {item.name}
            </Option>
          ))}
        </Select>
      ),
      name: ['profile'],
      label: translate('curMg.profile'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input placeholder={translate('curMg.names')} />,
      name: ['name'],
      label: translate('curMg.names'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input placeholder={translate('curMg.lastNames')} />,
      name: ['lastName'],
      label: translate('curMg.lastNames'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: (
        <Select
          showSearch
          placeholder={translate('curMg.defaultClinic')}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {clinicsList?.map((item, index) => (
            <Option key={index} value={item.codigo_id}>
              {item.nombre}
            </Option>
          ))}
        </Select>
      ),
      name: ['clinicId'],
      label: translate('curMg.defaultClinic'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input placeholder={translate('curMg.email')} />,
      name: ['email'],
      label: translate('curMg.email'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    }
  ];

  return (
    <div className="flex flex-col h-full px-7 pt-5 pb-7 ">
      <FormGenerator
        initialValues={{}}
        formParam={form}
        onFinish={onFinish}
        items={formItems}
        bottomOptions={bottomOptionsCreateEdit(
          onGoBack,
          translate,
          'create',
          styles.actionButton
        )}
      />
    </div>
  );
};

/**
 * Component definition of proptypes.
 */
CountryUserRolesCreationView.propTypes = {
  countryUserRoleActions: PropTypes.shape({
    createCountryUserRole: PropTypes.func.isRequired
  }).isRequired,
  roleActions: PropTypes.shape({
    getRolesArray: PropTypes.func.isRequired
  }).isRequired,
  authActions: PropTypes.shape({
    getUserList: PropTypes.func.isRequired
  }).isRequired,
  profileActions: PropTypes.shape({
    getProfilesArray: PropTypes.func.isRequired
  }).isRequired,
  dashboardStaffActions: PropTypes.shape({
    getAdmonCodesByEntity: PropTypes.func.isRequired
  })
};

export default CountryUserRolesCreationView;
